import { useEffect } from "react";
import { create } from "zustand";
import type {
  FapsChestDeal,
  FapsChestResponse,
  FapsKind,
  FapsRedeemResponse,
  FapsWalletResponse,
  User,
} from "@gooner/shared";
import { track } from "../analytics";
import { userApi } from "./api";
import { usePublicSettings } from "./publicSettings";
import { useSession } from "./session";

/**
 * FAPS, client side: the wallet store, the hooks every surface reads, and the funnel's
 * vocabulary.
 *
 * NAMING, because the word was already taken twice. A *fap* is the act — the crown on a
 * tile. *Your faps* is the collection it lands in (/collections/faps). **Faps** —
 * capitalised — is the currency, and in the bar it is not spelled at all: the crown
 * beside a number IS a Faps balance, site-wide. Copy that mixes them reads as a typo,
 * so this file is the one place the rule is written down.
 *
 * TWO RULES the store lives by:
 *
 *  1. THE SERVER'S NUMBER IS THE NUMBER. Every earn and spend answers with the balance,
 *     and the store takes it verbatim — no local arithmetic that could drift from the
 *     ledger. The one local touch is the day's fap counter, corrected on the next read.
 *  2. DARK MEANS INVISIBLE. `useFapsEnabled` is false until the settings answer, the
 *     OPPOSITE of useGoldGate's permissive default: a pill advertising a currency this
 *     box does not have is worse than a pill arriving one tick late, and nothing is
 *     refused by the delay.
 */

export type FapsWallet = FapsWalletResponse;

interface FapsState {
  /** null = not fetched yet, or signed out. */
  wallet: FapsWallet | null;
  fetchedAt: number;
  inflight: Promise<void> | null;
  /**
   * The welcome, latched. The wallet carries `welcomed` on exactly one read (the grant's);
   * every later refresh replaces the wallet without it, so the moment lives here until
   * the card is dismissed. Cleared on sign-out with the rest.
   */
  welcomePending: boolean;
  dismissWelcome: () => void;
  /**
   * When a tap last came back capped — the day's crown take is full. The nudge that sells
   * Premium's higher cap reads this; nothing else does. Null until it happens.
   */
  cappedAt: number | null;
  /**
   * The most recent time this account was UNSEATED — a wall square of theirs was taken over
   * and they were paid the cut — that they have not acknowledged. Latched from the newest
   * `unseated` ledger row a refresh sees, above a localStorage high-water mark, so it
   * arrives on the next wallet poll after being sniped (there is no per-user push). The
   * take-it-back card reads this; nothing else does. `regionId` may be absent on a row that
   * predates the region-carrying ref. Null until it happens.
   */
  unseatedPending: { regionId?: string; note: string | null; cut: number; id: number } | null;
  /** Acknowledge the take-it-back card — raises the localStorage mark so it never returns. */
  dismissUnseated: () => void;
  /** GET /faps/wallet. Dedupes an in-flight read; `ifOlderThan` skips a fresh one. */
  refresh: (opts?: { ifOlderThan?: number }) => Promise<void>;
  /**
   * A fap's reply, applied: the balance verbatim, the day's counter bumped by what the tap
   * actually paid (`fapsAmount`; a member's crown pays more than one), and the cap moment
   * recorded. Every crown call site hands the WHOLE reply here — including a tap that paid
   * nothing (`fapsEarned` 0, or absent on a toggle that landed OFF) — so this store, not
   * the caller, decides what each answer means.
   */
  applyClick: (r: { fapsEarned?: 0 | 1; fapsAmount?: number; fapsBalance?: number; capped?: true }) => void;
  /** A balance a server response carried verbatim, then a quiet re-read for the
   *  meters — the wall's defend dialog, which spends Faps outside the crown/chest
   *  doors above and has no other way to keep the pill in step. (Used to also apply
   *  /session/stop's settle share; that call site is gone with the settle itself —
   *  see FapsService.settleWatchThreshold — but the shape is still exactly what a
   *  spend-then-repaint caller needs.) */
  applyStop: (r: { balance: number }) => void;
  /** Deal today's chest. */
  dealChest: () => Promise<FapsChestDeal>;
  /** Pick a tile of a dealt chest — the prize lands and every tile is revealed. */
  pickChest: (dealId: string, index: number) => Promise<FapsChestResponse>;
  redeem: (code: string) => Promise<FapsRedeemResponse>;
  /** Sign-out. */
  reset: () => void;
}

export const useFapsStore = create<FapsState>((set, get) => ({
  wallet: null,
  fetchedAt: 0,
  inflight: null,
  welcomePending: false,
  cappedAt: null,
  unseatedPending: null,

  refresh: async (opts) => {
    const { inflight, fetchedAt } = get();
    if (inflight) return inflight;
    if (opts?.ifOlderThan !== undefined && Date.now() - fetchedAt < opts.ifOlderThan) return;
    const p = userApi
      .fapsWallet()
      .then((wallet) =>
        set((cur) => ({
          wallet,
          fetchedAt: Date.now(),
          // Latch, never clear: the flag rides one read and this is the only place it lands.
          welcomePending: cur.welcomePending || wallet.welcomed === true,
          unseatedPending: latchUnseated(cur.unseatedPending, wallet),
        })),
      )
      .catch(() => undefined)
      .finally(() => set({ inflight: null }));
    set({ inflight: p });
    return p;
  },

  dismissWelcome: () => set({ welcomePending: false }),

  dismissUnseated: () =>
    set((cur) => {
      const id = cur.unseatedPending?.id;
      if (id) {
        try {
          localStorage.setItem(UNSEATED_SEEN_KEY, String(id));
        } catch {
          // Blocked storage: the card can return next poll, the honest fallback.
        }
      }
      return { unseatedPending: null };
    }),

  applyClick: (r) => {
    if (r.capped) set({ cappedAt: Date.now() });
    const w = get().wallet;
    if (!w) return;
    // The clip meter moves by CLIPS, not Faps — one more distinct clip landed this tap,
    // or none (a re-crown of one already paid today, or the cap). `fapsEarned` is
    // exactly that 0|1; `fapsAmount` is the Faps a landed clip paid, which is a
    // different number the moment fapReward isn't 1 and belongs on the balance alone.
    const landed = r.fapsEarned ?? 0;
    set({
      wallet: {
        ...w,
        balance: r.fapsBalance ?? w.balance,
        today: {
          ...w.today,
          fap: { ...w.today.fap, earned: w.today.fap.earned + landed },
        },
      },
    });
  },

  applyStop: (r) => {
    const w = get().wallet;
    if (!w) return;
    set({ wallet: { ...w, balance: r.balance } });
    void get().refresh();
  },

  dealChest: () => userApi.fapsChestDeal(),

  pickChest: async (dealId, index) => {
    const r = await userApi.fapsChestPick(dealId, index);
    const w = get().wallet;
    if (w) {
      set({
        wallet: {
          ...w,
          balance: r.balance,
          chest: { ...w.chest, available: false },
        },
      });
    }
    // The ledger rows changed too; a quiet re-read brings them without blocking the reveal.
    void get().refresh();
    return r;
  },

  redeem: async (code) => {
    const r = await userApi.fapsRedeem(code);
    const w = get().wallet;
    if (w) set({ wallet: { ...w, balance: r.balance } });
    void get().refresh();
    return r;
  },

  reset: () => set({ wallet: null, fetchedAt: 0, welcomePending: false, cappedAt: null, unseatedPending: null }),
}));

/** The localStorage high-water mark: the id of the last unseated row the card acknowledged.
 *  One key, a number — a newer unseated id clears it; blocked storage costs a repeat. */
const UNSEATED_SEEN_KEY = "gp:faps-unseated-seen";

/** Pick the take-it-back card's subject: the newest `unseated` ledger row above the seen
 *  mark. Keeps the one already showing if the wallet has nothing newer, so an ordinary
 *  poll does not blink the card away. */
function latchUnseated(
  current: FapsState["unseatedPending"],
  wallet: FapsWallet,
): FapsState["unseatedPending"] {
  // `recent` is newest-first, so the first unseated row is the latest.
  const u = wallet.recent.find((r) => r.kind === "unseated");
  if (!u) return current;
  let seen = 0;
  try {
    seen = Number(localStorage.getItem(UNSEATED_SEEN_KEY) ?? "0") || 0;
  } catch {
    // Blocked storage: treat as never-seen; the card shows once per load.
  }
  if (u.id <= seen) return null;
  if (current?.id === u.id) return current;
  return { regionId: u.regionId, note: u.note, cut: u.delta, id: u.id };
}

/** The switch, as this page load knows it. FALSE until the settings answer — rule 2. */
export function useFapsEnabled(): boolean {
  const settings = usePublicSettings();
  return settings?.faps?.enabled ?? false;
}

/** Everything a surface needs to render FAPS: the switch, who, and the wallet. */
export function useFaps(): {
  enabled: boolean;
  user: User | null;
  wallet: FapsWallet | null;
  /** null while unknown — the pill shows a placeholder, never a wrong number. */
  balance: number | null;
  chestReady: boolean;
  refresh: FapsState["refresh"];
  dealChest: FapsState["dealChest"];
  pickChest: FapsState["pickChest"];
  redeem: FapsState["redeem"];
} {
  const enabled = useFapsEnabled();
  const user = useSession((s) => s.user);
  const wallet = useFapsStore((s) => s.wallet);
  const refresh = useFapsStore((s) => s.refresh);
  const dealChest = useFapsStore((s) => s.dealChest);
  const pickChest = useFapsStore((s) => s.pickChest);
  const redeem = useFapsStore((s) => s.redeem);
  return {
    enabled,
    user,
    wallet,
    balance: wallet ? wallet.balance : user?.fapsBalance ?? null,
    chestReady: wallet?.chest.available ?? false,
    refresh,
    dealChest,
    pickChest,
    redeem,
  };
}

/**
 * THE ONE FETCH DRIVER. Mounted by the pill — which the bar puts on every surface — so
 * nothing else has to remember to read the wallet. Reads on mount and on a change of
 * who is signed in; re-reads when the tab comes back (the chest crosses UTC midnight
 * and watch time accrues server-side while the tab is elsewhere), but only if the last
 * read is a minute old. No interval: earns and spends carry the balance in their own
 * replies, and the one place that mints without a click — a running goon session —
 * ticks its own 5-minute refresh from DisplayApp, the cadence the server pays at.
 */
export function useFapsWalletSync(): void {
  const enabled = useFapsEnabled();
  const userId = useSession((s) => s.user?.id ?? null);
  const refresh = useFapsStore((s) => s.refresh);
  const reset = useFapsStore((s) => s.reset);
  useEffect(() => {
    if (!enabled || !userId) {
      reset();
      return;
    }
    void refresh();
    const onWake = () => {
      if (document.visibilityState === "visible") void refresh({ ifOlderThan: 60_000 });
    };
    document.addEventListener("visibilitychange", onWake);
    window.addEventListener("focus", onWake);
    return () => {
      document.removeEventListener("visibilitychange", onWake);
      window.removeEventListener("focus", onWake);
    };
  }, [enabled, userId, refresh, reset]);
}

// ---- copy, in one place ---------------------------------------------------------------

/** What the crown's sign-up nudge says, on the pill and the wall alike. */
export const FAPS_AUTH_REASON =
  "Create a free account to earn Faps — fap clips, open a daily chest, keep a streak.";
export const FAPS_WALL_AUTH_REASON = "Squares are bought with Faps, and Faps live on an account.";

/** A ledger row's sentence. The server ships the KIND; the words live here. */
export const LEDGER_COPY: Record<FapsKind, string> = {
  welcome: "Welcome to GoonLords",
  fap: "Fapped a clip",
  // Not "Goon session" any more (2026-09-09): watch-time pays off the day's total
  // playback, no session required — see FapsService.settleWatchThreshold.
  watch: "Watched 60 min",
  chest: "Daily chest",
  quest: "Daily quests",
  stipend: "Premium stipend",
  code: "Code redeemed",
  wall: "Wall square",
  // The displaced-holder cut — the note ("@sam took your marquee square") carries who and
  // which, so the copy is the short label the ledger shows beside it.
  unseated: "Square taken",
  // A step of vault space (2026-09-09) — the note carries how much.
  vault: "Vault space",
  admin: "From the house",
};

// ---- the funnel's closed vocabularies ---------------------------------------------------

// `link` is the /faps/redeem deep link — a code arriving from the Patreon shop's delivery.
// `vault_short` (2026-09-09): the vault's "Expand for 👑N" answered with a short balance —
// the second sink to open the wallet at the chest, kept apart from the wall's so the
// funnel can tell which sink is pulling.
export type FapsOpenSource = "pill" | "menu" | "wall_short" | "vault_short" | "profile" | "link";
export type FapsDoor = "chest" | "redeem" | "premium" | "shop";

export const trackFapsPillOpened = (source: FapsOpenSource): void => track("faps_pill_opened", { source });
export const trackFapsChestClaimed = (amount: number): void => track("faps_chest_claimed", { amount });
export const trackFapsCodeRedeemed = (amount: number): void => track("faps_code_redeemed", { amount });
/** Short for a Faps price — {needed, source}. `source` says which sink asked: the wall
 *  (the default, its two call sites unchanged) or the vault's expand step. */
export const trackFapsInsufficientShown = (needed: number, source: "wall" | "vault" = "wall"): void =>
  track("faps_insufficient_shown", { needed, source });
export const trackFapsGetMoreClicked = (door: FapsDoor): void => track("faps_get_more_clicked", { door });
/** The welcome card was shown — once per grant, so once per account, ever. No props. */
export const trackFapsWelcomeShown = (): void => track("faps_welcome_shown", {});
/** Which Faps surface sent someone to the wall. A closed vocabulary, like FapsDoor. */
export type FapsWallSource = "welcome" | "wallet" | "redeemed";
export type FapsTier = "basic" | "premium";
export const trackFapsCapHit = (tier: FapsTier): void => track("faps_cap_hit", { tier });
export const trackFapsCapNudgeShown = (): void => track("faps_cap_nudge_shown", {});
export const trackFapsCapNudgeClicked = (): void => track("faps_cap_nudge_clicked", {});
/** `short`: could they NOT afford a small square as they left for the wall. */
export const trackFapsWallClicked = (source: FapsWallSource, short: boolean): void =>
  track("faps_wall_clicked", { source, short });
/** The take-it-back card (bidding): a displaced holder saw it / tapped to reclaim. No props. */
export const trackFapsUnseatedShown = (): void => track("faps_unseated_shown", {});
export const trackFapsUnseatedReclaimClicked = (): void => track("faps_unseated_reclaim_clicked", {});
/** Every quest assigned today, finished — {amount}: the bundle actually paid (already
 *  doubled for Premium by the server, so the dialog quotes one number, not two). */
export const trackFapsQuestBundleCompleted = (amount: number): void =>
  track("faps_quest_completed", { amount });
